/**
 * WHAT WAS SAID — read only, on purpose.
 *
 * Anthony: "Comms need to work for things in the crm but we don't need to
 * be able to call or text from the crm yet."
 *
 * So there is no send function in this file and there should not be one
 * until sending is deliberately moved. The value of the ledger is that
 * anybody opening a customer can see the last conversation without
 * switching apps or asking a colleague, and that is worth having on its
 * own — reading is most of what the desk actually does with comms.
 *
 * MATCHED ON THE PHONE, NOT ONLY THE LINK. Most imported rows never had a
 * customer_id: the old system keyed them on the number. So a customer's
 * history is "rows linked to them, OR rows carrying their number", and
 * both halves come back as one list. phone10 is generated in the database
 * precisely so this is one comparison rather than five.
 */

import { db } from "./client.ts";
import type { Delivery } from "../domain/delivery.ts";

export type Comm = {
  id: string;
  kind: "call" | "sms" | "email" | "note" | "system";
  direction: "inbound" | "outbound";
  provider: string;
  phone: string | null;
  tracking_number: string | null;
  duration_seconds: number | null;
  recording_url: string | null;
  subject: string | null;
  body: string | null;
  occurred_at: string;
  /**
   * WHETHER AN OUTBOUND TEXT ACTUALLY REACHED THE HANDSET, read back from
   * GoHighLevel by server/tasks/delivery-watch.ts.
   *
   * Null on every inbound row, on anything that is not a GoHighLevel text,
   * and on every row written before 2026-09-19 -- null means nobody asked,
   * NEVER that it failed. The screen renders nothing for it, which is what
   * this whole column is here to stop being the answer for a message that
   * genuinely did not arrive.
   */
  delivery_status?: Delivery | null;
  /** The carrier's reason, when it gave one. Shown under the message. */
  delivery_detail?: string | null;
  /** The desk's rows say who at the shop was talking to the system. */
  /**
   * Whatever the sender knew and the columns had no room for. The desk's
   * rows carry who was talking; a CallRail row carries that service's own
   * read of the conversation, which is worth showing rather than storing.
   */
  raw?: {
    text_desk?: unknown; staff_name?: string; staff_role?: string; forwarded?: boolean;
    call_summary?: string | null; lead_score?: number | null;
    sentiment?: string | null; callrail_id?: string | null;
  } | null;
};

const SEL = "id,kind,direction,provider,phone,tracking_number,duration_seconds," +
  "recording_url,subject,body,occurred_at,delivery_status,delivery_detail,raw";

const ten = (s: string | null | undefined) => String(s ?? "").replace(/\D/g, "").slice(-10);

/** Everything said to or by one customer, however it was recorded. */
export async function commsForCustomer(
  customerId: string, phone: string | null, limit = 100,
): Promise<Comm[]> {
  const sb = await db();
  const p10 = ten(phone);

  const [byId, byPhone] = await Promise.all([
    sb.from("comm_events").select(SEL)
      .eq("customer_id", customerId).order("occurred_at", { ascending: false }).limit(limit),
    p10.length === 10
      ? sb.from("comm_events").select(SEL)
          .eq("phone10", p10).order("occurred_at", { ascending: false }).limit(limit)
      : Promise.resolve({ data: [] as unknown[] }),
  ]);

  // One list, no duplicates — a row can satisfy both halves.
  const seen = new Set<string>();
  const all: Comm[] = [];
  for (const r of [...((byId.data ?? []) as unknown as Comm[]), ...((byPhone.data ?? []) as unknown as Comm[])]) {
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    all.push(r);
  }
  return all.sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)).slice(0, limit);
}

/**
 * EVERYTHING SAID TO A NUMBER, FOR A LEAD THAT HAS NO CUSTOMER RECORD YET.
 *
 * Anthony: "pull ... any text message history from GHL" onto the lead card.
 *
 * commsForCustomer needs a customer id. A lead is exactly the thing that
 * does not have one -- somebody rang, they are on the board, and every text
 * they have exchanged with the shop is sitting in comm_events keyed only by
 * their number. So the card showed call notes and nothing else, while the
 * conversation itself was one query away.
 *
 * Oldest first, because this is read as a conversation rather than scanned
 * as a log.
 */
export async function commsForPhone(phone: string | null, limit = 60): Promise<Comm[]> {
  const p10 = ten(phone);
  if (p10.length !== 10) return [];
  const sb = await db();
  const { data, error } = await sb.from("comm_events").select(SEL)
    .eq("phone10", p10).order("occurred_at", { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as Comm[])
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
}

/** The whole shop's traffic, newest first. */
export async function recentComms(limit = 200): Promise<Comm[]> {
  const sb = await db();
  const { data, error } = await sb.from("comm_events")
    .select(`${SEL},customers(id,full_name)`)
    .order("occurred_at", { ascending: false }).limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as Comm[];
}

/** "4m 12s", because 252 is not a length anybody reads. */
export function spoken(seconds: number | null): string {
  if (!seconds || seconds < 1) return "";
  const m = Math.floor(seconds / 60), s = seconds % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}


/**
 * THE LINE THIS PERSON RANG.
 *
 * CallRail runs one tracking number per market, so the line somebody
 * called is the only reliable statement of which market a job belongs to.
 * Caller ID city is carrier data and the old system found 1,066 of them
 * were place names rather than cities.
 *
 * Null when they have never called a tracked line — a walk-in, or somebody
 * who only ever texted. The caller falls back to the area code then, which
 * is weaker and honest about being so.
 */
export async function trackingLineFor(phone: string | null): Promise<string | null> {
  const p10 = ten(phone);
  if (p10.length !== 10) return null;
  const sb = await db();
  const { data } = await sb.from("comm_events")
    .select("tracking_number")
    .eq("phone10", p10)
    .not("tracking_number", "is", null)
    .order("occurred_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return (data as { tracking_number: string } | null)?.tracking_number ?? null;
}
